import { useEffect, useState } from "react";
import { Loader2, Landmark, RefreshCw, Snowflake, Ban } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { getCapayAccountByCase, refreshCapayAccount } from "@/lib/api";
import { VaDetails, SettlementAccountForm } from "@/pages/aus/CapayAccountCard";

/** AUS customer file → "Settlement A/C": the customer's Capay Virtual Account
 *  (where manual arrears/settlement payments must go). */
export default function CapayAccountTab({ caseRow }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try { setData(await getCapayAccountByCase(caseRow.case_id)); }
    catch (e) { setError(e?.response?.data?.detail || "Could not load Capay account"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [caseRow.case_id]);

  const refresh = async () => {
    setBusy(true);
    try { const acc = await refreshCapayAccount(data.account.sf_number); setData((d) => ({ ...d, account: acc })); toast.success("Refreshed from Capay"); }
    catch { toast.error("Refresh failed"); } finally { setBusy(false); }
  };

  if (loading) return <div className="border rounded-lg bg-white p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Loading settlement account…</div>;
  if (error) return <div className="border rounded-lg bg-white p-6 text-sm text-red-700" data-testid="capay-tab-error">{error}</div>;
  const acc = data?.account;
  const snap = data?.disbursement_snapshot;
  const p = data?.prefill || {};
  // A previous failed attempt (e.g. Capay-side block) leaves the request we sent — reuse it
  const pp = acc?.payload_preview || {};
  const initial = {
    company_name: p.company_name, legal_name: p.legal_name || pp.name, trading_name: p.trading_name || pp.tradingName,
    abn: p.abn || acc?.abn || pp.businessNumber, acn: p.acn || pp.companyNumber,
    is_sole_trader: !!p.is_sole_trader, business_type: p.business_type || pp.businessType, register_date: p.register_date || pp.registerDate,
    beneficial_owner_name: p.beneficial_owner_name || [pp.beneficiaryOwnerFirstName, pp.beneficiaryOwnerLastName].filter(Boolean).join(" "),
    contact_email: p.contact_email || pp.contactEmail,
    street: pp.address?.street || "", city: pp.address?.city || "", state: pp.address?.state || p.address?.state || "",
    zip: pp.address?.zip || p.address?.zip || "", address_line: pp.address?.street ? "" : (p.address?.line || ""),
  };

  const disabled = acc && acc.status && acc.status !== "active";
  const hasLiveVa = !!acc?.virtual_account?.account_number && !disabled;
  const createSf = p.sf_number || caseRow.loan_application_id || "";
  const opts = data?.settlement_options?.options || [];
  const house = opts.find((o) => o.key === "capay");

  return (
    <div className="border rounded-lg bg-white p-4 space-y-4" data-testid="capay-account-tab">
      <div className="flex items-center gap-2">
        <Landmark className="w-4 h-4 text-emerald-700" />
        <h3 className="font-semibold">Settlement account</h3>
        {data?.mode && <Badge variant="outline" className={data.mode === "live" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-amber-50 text-amber-700 border-amber-300"}>{data.mode.toUpperCase()}</Badge>}
        {hasLiveVa && <Button size="sm" variant="ghost" className="ml-auto" onClick={refresh} disabled={busy} data-testid="capay-tab-refresh-btn"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} />Refresh</Button>}
      </div>
      <p className="text-sm text-muted-foreground">
        Customers can settle straight into the CaPay house account{house ? <> — <span className="font-medium text-slate-700">{house.account_name} · BSB {house.bsb} · {house.account_number}</span></> : null}.
        A dedicated settlement sub account is optional; when one exists it is offered first on payout letters, contracts and statements.
      </p>
      {hasLiveVa ? (
        <>
          <p className="text-sm">Capay customer <span className="font-mono">{acc.capay_client_no}</span> · <Badge variant="outline" data-testid="capay-tab-client-status">{acc.capay_client_status}</Badge> · created {acc.created_at?.slice(0, 10)} by {acc.created_by}
            {acc.linked_sf_numbers?.length ? <span className="text-xs text-muted-foreground"> · also used by {acc.linked_sf_numbers.join(", ")}</span> : null}</p>
          <VaDetails va={acc.virtual_account} sf={acc.sf_number} />
          {snap && (
            <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="capay-tab-snapshot"><Snowflake className="w-3.5 h-3.5" />Snapshotted on the deal at disbursement ({snap.snapshotted_at?.slice(0, 10)}) — BSB {snap.bsb} / {snap.account_number}</p>
          )}
        </>
      ) : (
        <>
          {disabled && acc?.virtual_account?.account_number && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-1 opacity-80" data-testid="capay-tab-disabled">
              <div className="flex items-center gap-2 text-sm">
                <Ban className="w-4 h-4 text-slate-500" />
                <span className="font-medium text-slate-700">Previous sub account</span>
                <Badge variant="outline" className="bg-slate-200 text-slate-700 border-slate-300" data-testid="capay-tab-status-badge">Disabled</Badge>
                <span className="text-xs text-muted-foreground">{acc.disabled_at ? `since ${acc.disabled_at.slice(0, 10)}` : acc.closed_at ? `closed ${acc.closed_at.slice(0, 10)}` : ""}</span>
              </div>
              <p className="text-xs text-slate-600 font-mono line-through decoration-slate-400" data-testid="capay-tab-disabled-details">
                {acc.virtual_account.account_name} · BSB {acc.virtual_account.bsb} · {acc.virtual_account.account_number}
              </p>
              <p className="text-xs text-muted-foreground">No longer printed on documents or used for matching. Kept for history.</p>
            </div>
          )}
          <SettlementAccountForm sfNumber={createSf} initial={initial} user={user}
            applicationId={p.application_id} caseId={caseRow.case_id}
            onAccount={(a) => setData((d) => ({ ...d, found: true, account: a }))} />
        </>
      )}
    </div>
  );
}
